import { ATTRIBUTE_OBJECTS, attributeField, CLICK_FREQUENCIES, consoleRoutes, parseAttributeField, parseSelectors, SELECTOR_LIMITS, type ClickFrequency, type WatchedAttribute } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Field, Input, Select } from "@sagot/ui";
import { useWorkspaceContext } from "../../../../session/workspace-context";
import { attributesOf } from "../../../workspace/shared";
import { problemText } from "../problem-model";
import type { TriggerOptions } from "../types";
import { useProblems } from "./problems-context";
import { useRefs } from "./refs";

const O = "engage.workflows.builder.options";
const P = "engage.workflows.extras.problems";

interface FieldProps {
  options: TriggerOptions;
  disabled: boolean;
  onChange: (options: TriggerOptions) => void;
}

/** Settings problems the fields above do not show themselves (nothing chosen yet, a channel the trigger cannot use). */
const OWN_PROBLEMS: ReadonlySet<string> = new Set(["needs_attribute", "needs_selector", "clicks_widget_only", "bad_frequency"]);

export function TriggerProblems() {
  const { t } = useTranslation();
  const { all } = useProblems();
  const own = all.filter((problem) => problem.where === "settings" && OWN_PROBLEMS.has(problem.code));
  if (own.length === 0) return null;
  return (
    <ul className="space-y-1 text-xs font-semibold text-danger" data-testid="trigger-problems">
      {own.map((problem) => <li key={problem.code}>{problemText(problem, t)}</li>)}
    </ul>
  );
}

/** The attribute an `attribute_changed` workflow watches, picked from the workspace's defined attributes. */
export function WatchedAttributeField({ options, disabled, onChange }: FieldProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const { attributes, loading } = useRefs();
  const watched = options.attribute;
  const current = watched ? attributeField(watched.object, watched.key) : "";
  const defined = ATTRIBUTE_OBJECTS.some((object) => attributesOf(attributes, object).length > 0);
  const known = watched ? attributes.some((item) => item.object === watched.object && item.key === watched.key) : true;
  if (!defined && !loading && !watched) {
    return (
      <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="attribute-none">
        {t(`${O}.attributeNone`)}{" "}
        <AppLink href={consoleRoutes.settings(summary.slug, "attributes")} className="font-semibold text-primary-ink underline">{t(`${O}.attributeNoneLink`)}</AppLink>
      </p>
    );
  }
  const pick = (field: string) => {
    const parsed = parseAttributeField(field);
    onChange({ ...options, attribute: parsed ? ({ object: parsed.object, key: parsed.key } satisfies WatchedAttribute) : undefined });
  };
  return (
    <div className="space-y-2">
      <Field label={t(`${O}.attribute`)} hint={t(`${O}.attributeHint`)}>
        <Select disabled={disabled} value={current} onChange={(e) => { pick(e.target.value); }} data-testid="watched-attribute">
          <option value="">{t(`${O}.attributeChoose`)}</option>
          {known || !watched ? null : <option value={current}>{t(`${O}.attributeGone`, { label: `${watched.object}.${watched.key}` })}</option>}
          {ATTRIBUTE_OBJECTS.map((object) => {
            const items = attributesOf(attributes, object);
            if (items.length === 0) return null;
            return (
              <optgroup key={object} label={t(`${O}.attributeGroups.${object}`)}>
                {items.map((item) => <option key={item.key} value={attributeField(object, item.key)}>{item.label}</option>)}
              </optgroup>
            );
          })}
        </Select>
      </Field>
      {watched && watched.object !== "conversation" ? <p className="text-xs text-fg-subtle">{t(`${O}.attributeNoConversation`)}</p> : null}
      <p className="text-xs text-fg-subtle">{t(`${O}.attributeFacts`)}</p>
    </div>
  );
}

/** The element a click starts the workflow on: a safe CSS selector, checked as it is typed (the server checks it again). */
export function ClickSelectorField({ options, disabled, onChange }: FieldProps) {
  const { t } = useTranslation();
  const value = options.selector ?? "";
  const parsed = parseSelectors(value);
  const typed = value.trim() !== "";
  const error = typed && parsed.error !== null ? t(`${P}.${parsed.error === "empty" ? "needs_selector" : `selector_${parsed.error}`}`) : undefined;
  const hint = parsed.selectors ? `${t(`${O}.selectorHint`)} ${t(`${O}.selectorCount`, { count: parsed.selectors.length })}` : t(`${O}.selectorHint`);
  return (
    <div className="space-y-2">
      <Field label={t(`${O}.selector`)} hint={hint} {...(error ? { error } : {})}>
        <Input
          className="font-mono text-xs"
          maxLength={SELECTOR_LIMITS.field}
          placeholder={t(`${O}.selectorPlaceholder`)}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={disabled}
          value={value}
          onChange={(e) => { onChange({ ...options, selector: e.target.value }); }}
        />
      </Field>
      {parsed.selectors ? (
        <details className="rounded-md bg-sunken p-3 text-xs" data-testid="selector-test">
          <summary className="cursor-pointer font-semibold">{t(`${O}.selectorTest`)}</summary>
          <p className="mt-2 text-fg-muted">{t(`${O}.selectorTestBody`)}</p>
          <code className="mt-2 block select-all break-all font-mono">{`document.querySelectorAll(${JSON.stringify(parsed.selectors.join(", "))})`}</code>
        </details>
      ) : null}
    </div>
  );
}

/** How often a click may start the workflow for one visitor. */
export function ClickFrequencyField({ options, disabled, onChange }: FieldProps) {
  const { t } = useTranslation();
  const current: ClickFrequency = CLICK_FREQUENCIES.find((frequency) => frequency === options.frequency) ?? "every_click";
  return (
    <Field label={t(`${O}.clickFrequency`)}>
      <Select disabled={disabled} value={current} onChange={(e) => { onChange({ ...options, frequency: e.target.value as ClickFrequency }); }}>
        {CLICK_FREQUENCIES.map((frequency) => <option key={frequency} value={frequency}>{t(`${O}.clickFrequencies.${frequency}`)}</option>)}
      </Select>
    </Field>
  );
}

/** Clicks come from the website widget on a page; the other channels never report them. */
export function WidgetOnlyNote() {
  const { t } = useTranslation();
  return <p className="rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="widget-only-note">{t(`${O}.widgetOnly`)}</p>;
}

/** Triggers that cannot be saved before the person says what to watch or which element to click. */
export const needsStartOptions = (trigger: string): boolean => trigger === "attribute_changed" || trigger === "visitor_clicks_element";

/** Whether the options the person set while creating the workflow are enough for the server to accept it. */
export function startOptionsReady(trigger: string, options: TriggerOptions): boolean {
  if (trigger === "attribute_changed") return options.attribute !== undefined;
  if (trigger === "visitor_clicks_element") return parseSelectors(options.selector ?? "").error === null;
  return true;
}

/** What the create dialog asks for under the trigger list: the attribute to watch, or the element to click. */
export function TriggerStartOptions({ trigger, options, onChange }: { trigger: string; options: TriggerOptions; onChange: (options: TriggerOptions) => void }) {
  const { t } = useTranslation();
  if (!needsStartOptions(trigger)) return null;
  return (
    <section className="space-y-3 rounded-lg border border-line p-4" aria-label={t(`${O}.startTitle`)} data-testid="trigger-start-options">
      <p className="text-sm font-bold">{t(`${O}.startTitle`)}</p>
      {trigger === "attribute_changed" ? <WatchedAttributeField options={options} disabled={false} onChange={onChange} /> : <ClickSelectorField options={options} disabled={false} onChange={onChange} />}
    </section>
  );
}
